import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useTranslations } from "next-intl"
import {
	Newspaper, Search, ExternalLink, Copy, Check, CheckCircle2,
	X, Loader2, RefreshCw, Calendar, TrendingUp, Linkedin, Brain,
	ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Globe,
	ArrowUpDown, CircleDot,
} from "lucide-react"
import { toast } from "sonner"
import { DashboardLayout } from "@/components/common/DashboardLayout"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { FilterSheet, FilterTriggerButton } from "@/components/ui/filter-sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetDescription,
} from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { api } from "@/lib/apiService"
import { formatDate, getRelevanceBgColor } from "@/lib/utils"
import type { FilterGroupDefinition, FilterSelections } from "@/types/filters"

// ── Types ──

interface NewsSource {
	id: string
	source_name: string
	source_url: string
	published_at: string | null
}

interface NewsItem {
	id: string
	title: string
	summary: string
	relevance_score: number
	relevance_reason: string
	linkedin_post: string
	linkedin_angle: string
	status: "new" | "published" | "dismissed"
	published_at: string | null
	fetched_at: string
	published_linkedin_at: string | null
	sources: NewsSource[]
}

interface NewsListResponse {
	success: boolean
	data: NewsItem[]
	total: number
	page: number
	limit: number
	pages: number
}

interface NewsStats {
	total: number
	byStatus: Record<string, number>
	todayCount: number
	avgRelevanceScore: number
}

interface PipelineStatus {
	running: boolean
	currentPhase: string | null
	startedAt: string | null
	lastCompletedAt: string | null
	lastResult: {
		duration: string
		totalCandidates: number
		selectedNews: number
		savedNews: number
	} | null
	lastError: string | null
	schedule: string
}

// ── Filter helpers ──

interface NewsFilters {
	search: string
	status: string[]
	minScore: string
	maxScore: string
	fetchedFrom: string
	fetchedTo: string
	publishedFrom: string
	publishedTo: string
}

const DEFAULT_FILTERS: NewsFilters = {
	search: "",
	status: [],
	minScore: "",
	maxScore: "",
	fetchedFrom: "",
	fetchedTo: "",
	publishedFrom: "",
	publishedTo: "",
}

function countActiveFilters(filters: NewsFilters): number {
	let count = 0
	if (filters.status.length > 0) count++
	if (filters.minScore || filters.maxScore) count++
	if (filters.fetchedFrom || filters.fetchedTo) count++
	if (filters.publishedFrom || filters.publishedTo) count++
	return count
}

function filtersToSelections(filters: NewsFilters): FilterSelections {
	return {
		status: filters.status,
		score: [filters.minScore, filters.maxScore],
		fetchedDate: [filters.fetchedFrom, filters.fetchedTo],
		publishedDate: [filters.publishedFrom, filters.publishedTo],
	}
}

function selectionsToFilters(selections: FilterSelections, current: NewsFilters): NewsFilters {
	return {
		...current,
		status: selections.status ?? [],
		minScore: selections.score?.[0] ?? "",
		maxScore: selections.score?.[1] ?? "",
		fetchedFrom: selections.fetchedDate?.[0] ?? "",
		fetchedTo: selections.fetchedDate?.[1] ?? "",
		publishedFrom: selections.publishedDate?.[0] ?? "",
		publishedTo: selections.publishedDate?.[1] ?? "",
	}
}

// ── Main Page ──

export default function AiNewsPage() {
	const t = useTranslations()

	// State
	const [news, setNews] = useState<NewsItem[]>([])
	const [loading, setLoading] = useState(true)
	const [total, setTotal] = useState(0)
	const [page, setPage] = useState(1)
	const [pages, setPages] = useState(1)
	const [stats, setStats] = useState<NewsStats | null>(null)
	const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null)
	const pipelinePollRef = useRef<ReturnType<typeof setInterval> | null>(null)

	// Filters
	const [filters, setFilters] = useState<NewsFilters>(DEFAULT_FILTERS)
	const [filterOpen, setFilterOpen] = useState(false)
	const [sortBy, setSortBy] = useState("fetched_at")
	const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc")

	// Detail sheet
	const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null)
	const [copiedId, setCopiedId] = useState<string | null>(null)

	const activeFilterCount = countActiveFilters(filters)
	const filterSelections = useMemo(() => filtersToSelections(filters), [filters])

	const filterGroups: FilterGroupDefinition[] = useMemo(() => [
		{
			key: "status",
			label: t("aiNews.filterPanel.status"),
			icon: CircleDot,
			type: "multi-select" as const,
			options: [
				{ value: "new", label: t("aiNews.status.new") },
				{ value: "published", label: t("aiNews.status.published") },
				{ value: "dismissed", label: t("aiNews.status.dismissed") },
			],
		},
		{
			key: "score",
			label: t("aiNews.filterPanel.relevanceScore"),
			icon: Brain,
			type: "number-range" as const,
			options: [],
			numberRangeLabels: {
				min: t("aiNews.filterPanel.scoreMin"),
				max: t("aiNews.filterPanel.scoreMax"),
				minPlaceholder: "0",
				maxPlaceholder: "100",
			},
		},
		{
			key: "fetchedDate",
			label: t("aiNews.filterPanel.fetchedDate"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
		{
			key: "publishedDate",
			label: t("aiNews.filterPanel.publishedDate"),
			icon: Calendar,
			type: "date-range" as const,
			options: [],
		},
	], [t])

	const handleApplyFilters = (selections: FilterSelections) => {
		setFilters(selectionsToFilters(selections, filters))
		setPage(1)
	}

	const handleClearFilters = () => {
		setFilters(DEFAULT_FILTERS)
		setPage(1)
	}

	// Fetch news
	const fetchNews = useCallback(async () => {
		setLoading(true)
		try {
			const params = new URLSearchParams({
				page: String(page),
				limit: "15",
				sortBy,
				sortOrder,
			})
			if (filters.status.length > 0) params.set("status", filters.status.join(","))
			if (filters.search) params.set("search", filters.search)
			if (filters.minScore) params.set("minScore", filters.minScore)
			if (filters.maxScore) params.set("maxScore", filters.maxScore)
			if (filters.fetchedFrom) params.set("fetchedFrom", filters.fetchedFrom)
			if (filters.fetchedTo) params.set("fetchedTo", filters.fetchedTo)
			if (filters.publishedFrom) params.set("publishedFrom", filters.publishedFrom)
			if (filters.publishedTo) params.set("publishedTo", filters.publishedTo)

			const data = await api.get<NewsListResponse>(`/news?${params}`)
			setNews(data.data)
			setTotal(data.total)
			setPages(data.pages)
		} catch (err) {
			console.error("Failed to fetch news:", err)
			toast.error(t("aiNews.errors.fetchFailed"))
		} finally {
			setLoading(false)
		}
	}, [page, filters, sortBy, sortOrder, t])

	// Fetch stats
	const fetchStats = useCallback(async () => {
		try {
			const data = await api.get<{ success: boolean; data: NewsStats }>("/news/stats/summary")
			setStats(data.data)
		} catch {
			// Non-critical
		}
	}, [])

	// Fetch pipeline status
	const fetchPipelineStatus = useCallback(async () => {
		try {
			const data = await api.get<{ success: boolean; data: PipelineStatus }>("/news/pipeline/status")
			setPipelineStatus(data.data)
		} catch {
			// Non-critical
		}
	}, [])

	useEffect(() => {
		fetchNews()
	}, [fetchNews])

	useEffect(() => {
		fetchStats()
		fetchPipelineStatus()
	}, [fetchStats, fetchPipelineStatus])

	// Search debounce
	const [searchInput, setSearchInput] = useState("")
	useEffect(() => {
		const timer = setTimeout(() => {
			setFilters((prev) => ({ ...prev, search: searchInput }))
			setPage(1)
		}, 400)
		return () => clearTimeout(timer)
	}, [searchInput])

	// Actions
	const updateStatus = async (id: string, newStatus: "published" | "dismissed" | "new") => {
		try {
			await api.patch(`/news/${id}/status`, { status: newStatus })
			setNews((prev) =>
				prev.map((n) =>
					n.id === id
						? { ...n, status: newStatus, published_linkedin_at: newStatus === "published" ? new Date().toISOString() : null }
						: n
				)
			)
			fetchStats()
			toast.success(
				newStatus === "published"
					? t("aiNews.toast.markedPublished")
					: newStatus === "dismissed"
						? t("aiNews.toast.dismissed")
						: t("aiNews.toast.restored")
			)
		} catch {
			toast.error(t("aiNews.errors.updateFailed"))
		}
	}

	const copyToClipboard = async (text: string, newsId: string) => {
		try {
			await navigator.clipboard.writeText(text)
			setCopiedId(newsId)
			toast.success(t("aiNews.toast.copied"))
			setTimeout(() => setCopiedId(null), 2000)
		} catch {
			toast.error(t("aiNews.errors.copyFailed"))
		}
	}

	// Cleanup pipeline polling on unmount
	useEffect(() => {
		return () => {
			if (pipelinePollRef.current) clearInterval(pipelinePollRef.current)
		}
	}, [])

	const triggerPipeline = async () => {
		try {
			await api.post("/news/pipeline/trigger", {})
			toast.success(t("aiNews.toast.pipelineTriggered"))
			fetchPipelineStatus()

			// Clear any existing poll before starting a new one
			if (pipelinePollRef.current) clearInterval(pipelinePollRef.current)

			// Poll for completion — max 5 minutes
			let attempts = 0
			const MAX_ATTEMPTS = 60 // 5 min at 5s interval
			pipelinePollRef.current = setInterval(async () => {
				attempts++
				try {
					const data = await api.get<{ success: boolean; data: PipelineStatus }>("/news/pipeline/status")
					setPipelineStatus(data.data)
					if (!data.data.running || attempts >= MAX_ATTEMPTS) {
						if (pipelinePollRef.current) clearInterval(pipelinePollRef.current)
						pipelinePollRef.current = null
						if (data.data.lastError) {
							toast.error(`Pipeline error: ${data.data.lastError}`)
						} else if (data.data.lastResult) {
							const { savedNews, totalCandidates } = data.data.lastResult
							if (savedNews > 0) {
								toast.success(`${savedNews} notícies guardades`)
							} else {
								toast.warning(`Pipeline completat: 0 notícies guardades de ${totalCandidates} candidates`)
							}
						}
						fetchNews()
						fetchStats()
					}
				} catch {
					// Network error during polling — stop after too many failures
					if (attempts >= MAX_ATTEMPTS) {
						if (pipelinePollRef.current) clearInterval(pipelinePollRef.current)
						pipelinePollRef.current = null
					}
				}
			}, 5000)
		} catch {
			toast.error(t("aiNews.errors.pipelineFailed"))
		}
	}

	// Score badge helper
	const getScoreBadge = (score: number) => {
		const colorClass = getRelevanceBgColor(score)
		return (
			<Badge variant="outline" className={`${colorClass} border font-semibold tabular-nums`}>
				{score}
			</Badge>
		)
	}

	// Status badge helper — "Nova" only if fetched within the last 24h
	const getStatusBadge = (status: string, fetchedAt: string) => {
		if (status === "published") {
			return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border pointer-events-none">{t("aiNews.status.published")}</Badge>
		}
		if (status === "dismissed") {
			return <Badge className="bg-gray-100 text-gray-500 border-gray-200 border pointer-events-none">{t("aiNews.status.dismissed")}</Badge>
		}
		const isRecent = Date.now() - new Date(fetchedAt).getTime() < 24 * 60 * 60 * 1000
		if (isRecent) {
			return <Badge className="bg-blue-50 text-blue-700 border-blue-200 border pointer-events-none">{t("aiNews.status.new")}</Badge>
		}
		return null
	}

	return (
		<DashboardLayout>
			{/* Header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
						<Newspaper className="h-6 w-6" />
						{t("aiNews.title")}
					</h1>
					<p className="text-sm text-muted-foreground mt-1">{t("aiNews.subtitle")}</p>
				</div>

				<Button
					variant="outline"
					size="sm"
					onClick={triggerPipeline}
					disabled={pipelineStatus?.running}
				>
					{pipelineStatus?.running ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("aiNews.pipeline.running")}
						</>
					) : (
						<>
							<RefreshCw className="h-4 w-4 mr-1.5" />
							{t("aiNews.pipeline.trigger")}
						</>
					)}
				</Button>
			</div>

			{/* Stats cards */}
			{stats && (
				<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
					<Card className="p-3">
						<div className="text-xs text-muted-foreground">{t("aiNews.stats.total")}</div>
						<div className="text-2xl font-semibold mt-0.5">{stats.total}</div>
					</Card>
					<Card className="p-3">
						<div className="text-xs text-muted-foreground">{t("aiNews.stats.today")}</div>
						<div className="text-2xl font-semibold mt-0.5">{stats.todayCount}</div>
					</Card>
					<Card className="p-3">
						<div className="text-xs text-muted-foreground">{t("aiNews.stats.avgScore")}</div>
						<div className="text-2xl font-semibold mt-0.5">{stats.avgRelevanceScore || "—"}</div>
					</Card>
					<Card className="p-3">
						<div className="text-xs text-muted-foreground">{t("aiNews.stats.published")}</div>
						<div className="text-2xl font-semibold mt-0.5">{stats.byStatus?.published || 0}</div>
					</Card>
				</div>
			)}

			{/* Search + Filters + Sort */}
			<div className="flex flex-col sm:flex-row gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("aiNews.filters.searchPlaceholder")}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						className="pl-9"
					/>
				</div>

				<FilterTriggerButton
					activeCount={activeFilterCount}
					label={t("aiNews.filterPanel.title")}
					onClick={() => setFilterOpen(true)}
				/>

				<Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1) }}>
					<SelectTrigger className="w-[200px]">
						<ArrowUpDown className="h-4 w-4 mr-1.5 text-muted-foreground" />
						<SelectValue placeholder={t("aiNews.filters.sortBy")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="fetched_at">{t("aiNews.sort.fetchedAt")}</SelectItem>
						<SelectItem value="relevance_score">{t("aiNews.sort.relevance")}</SelectItem>
						<SelectItem value="published_at">{t("aiNews.sort.publishedAt")}</SelectItem>
						<SelectItem value="published_linkedin_at">{t("aiNews.sort.linkedinPublished")}</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* News list */}
			{loading ? (
				<LoadingSpinner />
			) : news.length === 0 ? (
				<div className="text-center py-16 text-muted-foreground">
					<Newspaper className="h-12 w-12 mx-auto mb-3 opacity-30" />
					<p className="text-lg font-medium">{t("aiNews.empty.title")}</p>
					<p className="text-sm mt-1">{t("aiNews.empty.description")}</p>
				</div>
			) : (
				<div className="space-y-3">
					{news.map((item) => (
						<Card
							key={item.id}
							className={`p-4 transition-colors hover:bg-accent/30 cursor-pointer ${
								item.status === "dismissed" ? "opacity-50" : ""
							}`}
							onClick={() => setSelectedNews(item)}
						>
							<div className="flex gap-4">
								{/* Score */}
								<div className="hidden sm:flex flex-col items-center justify-center min-w-[56px]">
									<div className={`text-xl font-bold tabular-nums ${
										item.relevance_score >= 75
											? "text-emerald-600"
											: item.relevance_score >= 50
												? "text-amber-600"
												: "text-red-500"
									}`}>
										{item.relevance_score}
									</div>
									<span className="text-[10px] text-muted-foreground uppercase tracking-wider">score</span>
								</div>

								{/* Content */}
								<div className="flex-1 min-w-0">
									<div className="flex items-start justify-between gap-3">
										<h3 className="font-medium text-sm leading-snug line-clamp-2">{item.title}</h3>
										<div className="flex items-center gap-1.5 shrink-0">
											{getStatusBadge(item.status, item.fetched_at)}
											<span className="sm:hidden">{getScoreBadge(item.relevance_score)}</span>
										</div>
									</div>

									<p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{item.summary}</p>

									<div className="flex items-center gap-3 mt-2.5 flex-wrap">
										{/* Sources count */}
										<span className="text-xs text-muted-foreground flex items-center gap-1">
											<Globe className="h-3 w-3" />
											{item.sources.length} {item.sources.length === 1 ? t("aiNews.source") : t("aiNews.sources")}
										</span>

										{/* Date */}
										<span className="text-xs text-muted-foreground flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											{formatDate(item.published_at || item.fetched_at)}
										</span>

										{/* LinkedIn angle */}
										{item.linkedin_angle && (
											<span className="text-xs text-muted-foreground flex items-center gap-1">
												<TrendingUp className="h-3 w-3" />
												{item.linkedin_angle}
											</span>
										)}

										{/* Published on LinkedIn date */}
										{item.published_linkedin_at && (
											<span className="text-xs text-emerald-600 flex items-center gap-1">
												<Linkedin className="h-3 w-3" />
												{formatDate(item.published_linkedin_at)}
											</span>
										)}
									</div>
								</div>

								{/* Quick actions */}
								<div className="hidden md:flex flex-col gap-1 shrink-0">
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7"
													onClick={(e) => {
														e.stopPropagation()
														copyToClipboard(item.linkedin_post, item.id)
													}}
												>
													{copiedId === item.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
												</Button>
											</TooltipTrigger>
											<TooltipContent>{t("aiNews.actions.copyPost")}</TooltipContent>
										</Tooltip>

										{item.status !== "published" && (
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														onClick={(e) => {
															e.stopPropagation()
															updateStatus(item.id, "published")
														}}
													>
														<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
													</Button>
												</TooltipTrigger>
												<TooltipContent>{t("aiNews.actions.markPublished")}</TooltipContent>
											</Tooltip>
										)}

										{item.status !== "dismissed" && (
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														onClick={(e) => {
															e.stopPropagation()
															updateStatus(item.id, "dismissed")
														}}
													>
														<X className="h-3.5 w-3.5 text-red-500" />
													</Button>
												</TooltipTrigger>
												<TooltipContent>{t("aiNews.actions.dismiss")}</TooltipContent>
											</Tooltip>
										)}
									</TooltipProvider>
								</div>
							</div>
						</Card>
					))}
				</div>
			)}

			{/* Pagination */}
			{pages > 1 && (
				<div className="flex items-center justify-between">
					<p className="text-sm text-muted-foreground">
						{t("aiNews.pagination.showing", { from: (page - 1) * 15 + 1, to: Math.min(page * 15, total), total })}
					</p>
					<div className="flex items-center gap-1">
						<Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(1)} disabled={page === 1}>
							<ChevronsLeft className="h-4 w-4" />
						</Button>
						<Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page - 1)} disabled={page === 1}>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<span className="text-sm tabular-nums px-2">
							{page} / {pages}
						</span>
						<Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page + 1)} disabled={page === pages}>
							<ChevronRight className="h-4 w-4" />
						</Button>
						<Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(pages)} disabled={page === pages}>
							<ChevronsRight className="h-4 w-4" />
						</Button>
					</div>
				</div>
			)}

			{/* Filter Sheet */}
			<FilterSheet
				open={filterOpen}
				onOpenChange={setFilterOpen}
				title={t("aiNews.filterPanel.title")}
				groups={filterGroups}
				selections={filterSelections}
				onApply={handleApplyFilters}
				onClearAll={handleClearFilters}
				applyLabel={t("aiNews.filterPanel.apply")}
				clearAllLabel={t("aiNews.filterPanel.clear")}
				dateFromLabel={t("aiNews.filterPanel.from")}
				dateToLabel={t("aiNews.filterPanel.to")}
			/>

			{/* Detail Sheet */}
			<Sheet open={!!selectedNews} onOpenChange={(open) => !open && setSelectedNews(null)}>
				<SheetContent className="sm:max-w-[540px] overflow-y-auto">
					{selectedNews && (
						<>
							<SheetHeader>
								<div className="flex items-center gap-2 mb-1">
									{getScoreBadge(selectedNews.relevance_score)}
									{getStatusBadge(selectedNews.status, selectedNews.fetched_at)}
								</div>
								<SheetTitle className="text-left leading-snug">{selectedNews.title}</SheetTitle>
								<SheetDescription className="text-left">{selectedNews.summary}</SheetDescription>
							</SheetHeader>

							<div className="mt-6 space-y-6">
								{/* Relevance reason */}
								<div>
									<h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
										{t("aiNews.detail.relevance")}
									</h4>
									<p className="text-sm">{selectedNews.relevance_reason}</p>
								</div>

								{/* Sources */}
								<div>
									<h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
										{t("aiNews.detail.sources")} ({selectedNews.sources.length})
									</h4>
									<div className="space-y-1.5">
										{selectedNews.sources.map((source) => (
											<a
												key={source.id}
												href={source.source_url}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center gap-2 text-sm hover:text-blue-600 transition-colors group"
											>
												<ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-blue-600 shrink-0" />
												<span className="font-medium">{source.source_name}</span>
												{source.published_at && (
													<span className="text-xs text-muted-foreground">
														{formatDate(source.published_at)}
													</span>
												)}
											</a>
										))}
									</div>
								</div>

								{/* LinkedIn post */}
								{selectedNews.linkedin_post && (
									<div>
										<div className="flex items-center justify-between mb-1.5">
											<h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
												<Linkedin className="h-3.5 w-3.5" />
												{t("aiNews.detail.linkedinPost")}
											</h4>
											<Button
												variant="outline"
												size="sm"
												className="h-7 text-xs"
												onClick={() => copyToClipboard(selectedNews.linkedin_post, selectedNews.id)}
											>
												{copiedId === selectedNews.id ? (
													<><Check className="h-3 w-3 mr-1" />{t("aiNews.actions.copied")}</>
												) : (
													<><Copy className="h-3 w-3 mr-1" />{t("aiNews.actions.copy")}</>
												)}
											</Button>
										</div>
										{selectedNews.linkedin_angle && (
											<p className="text-xs text-muted-foreground mb-2 italic">
												{t("aiNews.detail.angle")}: {selectedNews.linkedin_angle}
											</p>
										)}
										<div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-wrap leading-relaxed border">
											{selectedNews.linkedin_post}
										</div>
									</div>
								)}

								{/* Actions */}
								<div className="flex gap-2 pt-2 border-t">
									{selectedNews.status !== "published" && (
										<Button
											className="flex-1"
											onClick={() => {
												updateStatus(selectedNews.id, "published")
												setSelectedNews({ ...selectedNews, status: "published" })
											}}
										>
											<CheckCircle2 className="h-4 w-4 mr-1.5" />
											{t("aiNews.actions.markPublished")}
										</Button>
									)}
									{selectedNews.status === "published" && (
										<Button
											variant="outline"
											className="flex-1"
											onClick={() => {
												updateStatus(selectedNews.id, "new")
												setSelectedNews({ ...selectedNews, status: "new" })
											}}
										>
											{t("aiNews.actions.unmarkPublished")}
										</Button>
									)}
									{selectedNews.status !== "dismissed" && (
										<Button
											variant="outline"
											className="flex-1"
											onClick={() => {
												updateStatus(selectedNews.id, "dismissed")
												setSelectedNews({ ...selectedNews, status: "dismissed" })
											}}
										>
											<X className="h-4 w-4 mr-1.5" />
											{t("aiNews.actions.dismiss")}
										</Button>
									)}
									{selectedNews.status === "dismissed" && (
										<Button
											variant="outline"
											className="flex-1"
											onClick={() => {
												updateStatus(selectedNews.id, "new")
												setSelectedNews({ ...selectedNews, status: "new" })
											}}
										>
											{t("aiNews.actions.restore")}
										</Button>
									)}
								</div>
							</div>
						</>
					)}
				</SheetContent>
			</Sheet>
		</DashboardLayout>
	)
}
